import { useState, useEffect } from 'react';
import { platformAPI } from '../services/api';
import {
  VEHICLE_TYPES as DEFAULT_VEHICLE_TYPES,
  ANY_VEHICLE_TYPE
} from '../config/vehicleTypes';

// Module-level cache so every component shares one fetch (no duplicate requests).
let cachedTypes = null;      // resolved list once fetched
let inflight = null;         // shared promise while fetching

const normalize = (list) =>
  (Array.isArray(list) ? list : [])
    .filter(t => t && t.value && t.label)
    .map(t => ({
      value: t.value,
      label: t.label,
      icon: t.icon || '🚘',
      category: t.category || 'four-wheeler',
      seats: t.seats || 4,
      isActive: t.isActive !== false
    }));

const fetchVehicleTypes = () => {
  if (cachedTypes) return Promise.resolve(cachedTypes);
  if (inflight) return inflight;
  inflight = platformAPI.getSettings()
    .then(res => {
      const list = normalize(res?.data?.settings?.vehicleTypes);
      cachedTypes = list.length ? list : normalize(DEFAULT_VEHICLE_TYPES);
      return cachedTypes;
    })
    .catch(() => {
      // On any failure, fall back to the bundled defaults — existing flow never breaks.
      return normalize(DEFAULT_VEHICLE_TYPES);
    })
    .finally(() => { inflight = null; });
  return inflight;
};

/**
 * Returns admin-managed vehicle types (from PlatformSettings), falling back to the
 * bundled config so selects always have data immediately.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.includeAny] - prepend the "Any Vehicle" option (for packages)
 * @param {boolean} [opts.includeInactive] - include hidden types (admin screens)
 */
export const useVehicleTypes = (opts = {}) => {
  const { includeAny = false, includeInactive = false } = opts;
  // Start with the bundled defaults so there is never an empty list during load.
  const [allTypes, setAllTypes] = useState(() => cachedTypes || normalize(DEFAULT_VEHICLE_TYPES));
  const [loading, setLoading] = useState(!cachedTypes);

  useEffect(() => {
    let mounted = true;
    fetchVehicleTypes().then(list => {
      if (mounted) {
        setAllTypes(list);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  const base = includeInactive ? allTypes : allTypes.filter(t => t.isActive !== false);
  const vehicleTypes = includeAny ? [ANY_VEHICLE_TYPE, ...base] : base;

  const getLabel = (value) =>
    [ANY_VEHICLE_TYPE, ...allTypes].find(t => t.value === value)?.label || ANY_VEHICLE_TYPE.label;

  return { vehicleTypes, allTypes, loading, getLabel };
};

// Allow other modules to invalidate the cache (e.g. after an admin edit).
export const invalidateVehicleTypesCache = () => { cachedTypes = null; inflight = null; };

export default useVehicleTypes;
